import React, { useEffect, useState } from "react";
import { Users, Eye, Globe2, MapPin } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";

const regionName = (() => {
  try { const dn = new Intl.DisplayNames(["sq"], { type: "region" }); return (cc) => (cc ? dn.of(cc) : "E panjohur"); }
  catch { return (cc) => cc || "E panjohur"; }
})();
const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "🌐");
const PERIODS = [["day", "Sot"], ["week", "7 ditë"], ["month", "30 ditë"], ["all", "Gjithsej"]];

function SummaryCard({ label, s }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-3">
      <div className="text-[11px] font-semibold text-slate-400 uppercase">{label}</div>
      <div className="flex items-center gap-1.5 mt-1"><Users className="w-4 h-4 text-emerald-500" /><span className="text-xl font-extrabold text-slate-900">{s.visitors}</span></div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500"><Eye className="w-3.5 h-3.5" />{s.views} hapje</div>
    </div>
  );
}

function RankList({ title, icon: Icon, rows, render, tid }) {
  const max = Math.max(1, ...rows.map((r) => r.visitors));
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={tid}>
      <h3 className="font-bold text-slate-800 flex items-center gap-1.5 mb-3"><Icon className="w-4 h-4 text-emerald-500" />{title}</h3>
      {!rows.length && <p className="text-sm text-slate-400">Ende pa të dhëna.</p>}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i}>
            <div className="flex justify-between text-sm"><span className="truncate text-slate-700">{render(r)}</span><span className="font-semibold text-slate-900 shrink-0 ml-2">{r.visitors} <span className="text-xs text-slate-400 font-normal">({r.views})</span></span></div>
            <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-1.5 bg-emerald-400 rounded-full" style={{ width: `${(r.visitors / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminStats() {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/site-stats?period=${period}`).then((r) => setD(r.data)).catch(() => {}); }, [period]);
  if (!d) return <p className="text-center text-slate-400 py-6 text-sm">Duke ngarkuar...</p>;
  const chart = d.daily.map((x) => ({ ...x, label: x.day.slice(8) + "." + x.day.slice(5, 7) }));
  return (
    <div className="space-y-4" data-testid="admin-stats">
      <div className="grid grid-cols-2 gap-2">
        {PERIODS.map(([k, l]) => <SummaryCard key={k} label={l} s={d.summary[k]} />)}
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="stats-chart">
        <h3 className="font-bold text-slate-800 mb-1">Vizitorët — 30 ditët e fundit</h3>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={4} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v, n) => [v, n === "visitors" ? "Vizitorë" : "Hapje"]} labelFormatter={(l) => `Data ${l}`} />
              <Bar dataKey="visitors" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {PERIODS.map(([k, l]) => (
          <button key={k} data-testid={`stats-period-${k}`} onClick={() => setPeriod(k)} className={`flex-1 text-xs font-semibold py-2 rounded-lg ${period === k ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <RankList tid="stats-countries" title="Shtetet" icon={Globe2} rows={d.countries} render={(r) => `${flag(r.cc)} ${regionName(r.cc)}`} />
      <RankList tid="stats-cities" title="Qytetet" icon={MapPin} rows={d.cities} render={(r) => `${flag(r.cc)} ${r.city || "E panjohur"}`} />
      <p className="text-[11px] text-slate-400">Numri i madh = vizitorë unikë, në kllapa = hapje faqesh. Vendndodhja llogaritet nga IP-ja dhe mund të jetë e përafërt.</p>
    </div>
  );
}
